import { useState, type ReactNode } from 'react';
import { IconArrowsExchange, IconChevronLeft, IconMoon, IconPlayerPlay, IconSun, IconArrowBackUp, IconX, IconCheck } from '@tabler/icons-react';
import { addMessages, useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Drawer, StateView, useToast, type ScreenState } from '@hana/ui';
import { PH_BACKUP, PH_CH, PH_DELEG0, PH_NOTI, PH_QUEUE, PH_QUICK, PH_SEATS, PH_SEAT_ROWS, PH_SESSIONS, phNotiOn, phNotiSum, type PhReq } from './settingsPhoneData';
import s from './settingsPhone.module.css';

addMessages('vi', {
  'setph.title': 'Cài đặt', 'setph.theme': 'Đổi giao diện sáng / tối', 'setph.photo': 'Đổi ảnh', 'setph.mee': 'Mở HANA Mee ↗', 'setph.meeSub': 'Chấm công, đơn, phiếu lương',
  'setph.retry': 'Thử lại', 'setph.errT': 'Không tải được cài đặt', 'setph.errS': 'Thay đổi chưa lưu vẫn giữ trên máy này',
  'setph.back': 'Về Cài đặt', 'setph.apprT': 'Duyệt thay đổi', 'setph.reject': 'Từ chối', 'setph.agree': 'Đồng ý', 'setph.selfAgree': 'Tự duyệt · cờ', 'setph.undo': 'Hoàn tác',
  'setph.tabPend': 'Chờ tôi · {n}', 'setph.tabDone': 'Đã xử lý', 'setph.filter': 'Lọc', 'setph.close': 'Đóng', 'setph.noWrite': 'Chưa mở ghi ở bản này',
  'setph.reason': 'Lý do từ chối (ít nhất 10 ký tự)', 'setph.rejectT': 'Từ chối {id}', 'setph.noPermT': 'Anh không có quyền duyệt thay đổi quản trị',
  'setph.apprErr': 'Không tải được hàng chờ', 'setph.apprErrS': 'Phiếu chờ vẫn giữ nguyên trên máy chủ',
});
addMessages('en', {
  'setph.title': 'Settings', 'setph.theme': 'Toggle light / dark', 'setph.photo': 'Change photo', 'setph.mee': 'Open HANA Mee ↗', 'setph.meeSub': 'Timekeeping, requests, payslips',
  'setph.retry': 'Retry', 'setph.errT': 'Could not load settings', 'setph.errS': 'Unsaved changes stay on this device',
  'setph.back': 'Back to Settings', 'setph.apprT': 'Approve changes', 'setph.reject': 'Reject', 'setph.agree': 'Approve', 'setph.selfAgree': 'Self-approve · flag', 'setph.undo': 'Undo',
  'setph.tabPend': 'Mine · {n}', 'setph.tabDone': 'Handled', 'setph.filter': 'Filter', 'setph.close': 'Close', 'setph.noWrite': 'Write is not enabled in this build',
  'setph.reason': 'Reason (at least 10 characters)', 'setph.rejectT': 'Reject {id}', 'setph.noPermT': 'You cannot approve admin changes',
  'setph.apprErr': 'Could not load the queue', 'setph.apprErrS': 'Pending requests are still on the server',
});

// Mint-Settings-Phone (390): danh sách nhóm; chạm dòng → bảng trượt; dòng Quản trị → màn Duyệt thay đổi. Dữ liệu mẫu, nút ghi chỉ đổi trên máy + toast.

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
const TONE: Record<string, string | undefined> = { pri: s.pri, warn: s.warn };

function Row({ k, sub, subWarn, r, badge, onTap }: { k: string; sub?: string; subWarn?: boolean; r?: string; badge?: string; onTap: () => void }) {
  return (
    <button type="button" className={s.row} onClick={onTap}>
      <span className={s.rl}><span className={s.rk}>{k}</span>{sub && <span className={`${s.rs} ${subWarn ? s.rsW : ''}`} style={{ display: 'block' }}>{sub}</span>}</span>
      {badge && <span className={s.badge}>{badge}</span>}
      <span className={s.rr}>{r}</span>
      <span className={s.chev} aria-hidden="true">›</span>
    </button>
  );
}
function Group({ h, children }: { h: string; children: ReactNode }) {
  return <div><div className={s.gH}>{h}</div><div className={s.grp}>{children}</div></div>;
}
function Sw({ on, label, locked, onChange }: { on: boolean; label: string; locked?: boolean; onChange: () => void }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} aria-disabled={locked || undefined} className={`${s.sw} ${on ? s.swOn : ''}`} onClick={onChange}><span /></button>;
}
function SRow({ k, sub, subWarn, children }: { k: string; sub?: string; subWarn?: boolean; children?: ReactNode }) {
  return <div className={s.shRow}><span className={s.rl}><span className={s.rk}>{k}</span>{sub && <span className={`${s.rs} ${subWarn ? s.rsW : ''}`} style={{ display: 'block' }}>{sub}</span>}</span>{children}</div>;
}

export function SettingsPhone() {
  const t = useT();
  const toast = useToast();
  const state: ScreenState = readState() ?? 'ready';
  const [view, setView] = useState<'list' | 'appr'>('list');
  const [sheet, setSheet] = useState<string | null>(null);
  const [dark, setDark] = useState(false);
  const [lang, setLang] = useState('vi');
  const [bub, setBub] = useState(true);
  const [dnd, setDnd] = useState(false);
  const [nOv, setNOv] = useState<Record<string, boolean>>({});
  const [bk, setBk] = useState<Record<string, string>>({});
  const [dl, setDl] = useState<readonly (readonly [string, string])[]>(state === 'empty' ? [] : PH_DELEG0);
  const [gone, setGone] = useState<string[]>([]);
  const [q, setQ] = useState<readonly PhReq[]>(state === 'empty' ? [] : PH_QUEUE);
  const [qTab, setQTab] = useState<'pend' | 'done'>('pend');
  const [rej, setRej] = useState<string | null>(null);
  const [why, setWhy] = useState('');
  const [whyTried, setWhyTried] = useState(false);
  const say = (msg: string) => toast({ msg });
  const sessions = PH_SESSIONS.filter((x) => !gone.includes(x[0]));
  const pend = q.filter((x) => x.st === 'pend' && x.mine);
  const noperm = state === 'noperm';

  const body = (() => {
    if (!sheet) return null;
    const [kind, id = ''] = sheet.split(':');
    if (kind === 'noti') {
      const n = PH_NOTI.find((x) => x.id === id) ?? PH_NOTI[0]!;
      return { title: n.k, sub: n.s, node: (
        <>
          {n.locked && <div className={`${s.shNote} ${s.shNoteW}`}>Khẩn P0 luôn gửi push + Zalo song song từ phút 0, không tắt được trong app.</div>}
          <div className={s.shRows}>
            {PH_CH.map((c, ci) => {
              const lk = !!n.locked && n.def[ci] === 1;
              const on = phNotiOn(n, ci, nOv);
              return <SRow key={c} k={c} sub={lk ? 'Luôn bật' : undefined} subWarn={lk}><Sw on={on} label={`${n.k} qua ${c}`} locked={lk} onChange={() => { if (lk) { say('Khẩn P0 không tắt được trong app'); return; } setNOv((o) => ({ ...o, [n.id + ci]: !on })); say(`${on ? 'Đã tắt' : 'Đã bật'} ${c} · ${n.k}`); }} /></SRow>;
            })}
          </div>
        </>) };
    }
    if (kind === 'hei') return { title: 'Bong bóng Hei', sub: '', node: (
      <div className={s.shRows}>
        <SRow k="Hiện bong bóng"><Sw on={bub} label="Hiện bong bóng" onChange={() => { setBub(!bub); say(bub ? 'Đã tắt bong bóng Hei' : 'Đã bật bong bóng Hei'); }} /></SRow>
        <SRow k="Không làm phiền"><Sw on={dnd} label="Không làm phiền" onChange={() => { setDnd(!dnd); say(dnd ? 'Đã tắt không làm phiền' : 'Đã bật không làm phiền'); }} /></SRow>
        <SRow k="Vị trí" sub="Cạnh cú" />
      </div>) };
    const pickList = (items: readonly (readonly [string, string])[], cur: string, set: (v: string) => void) => (
      <div className={s.shRows}>{items.map(([v, k]) => <button key={v} type="button" role="radio" aria-checked={v === cur} className={`${s.shRow} ${s.pick}`} onClick={() => set(v)}><span className={s.rl}><span className={s.rk}>{k}</span></span>{v === cur && <span className={s.ok} aria-hidden="true">✓</span>}</button>)}</div>
    );
    if (kind === 'theme') return { title: 'Chế độ màu', sub: 'Áp mọi màn và mọi máy', node: pickList([['light', 'Sáng'], ['dark', 'Tối']], dark ? 'dark' : 'light', (v) => setDark(v === 'dark')) };
    if (kind === 'lang') return { title: 'Ngôn ngữ', sub: 'Mã nghiệp vụ (BL, ETD, VGM) giữ tiếng Anh', node: pickList([['vi', 'Tiếng Việt'], ['en', 'English']], lang, (v) => { setLang(v); say('Đã đổi ngôn ngữ'); }) };
    if (kind === 'bk') {
      const b = PH_BACKUP.find((x) => x[0] === id) ?? PH_BACKUP[0]!;
      return { title: `Backup ghế ${b[0]}`, sub: 'Cùng phòng / cùng ghế · bỏ người đang nghỉ', node: pickList(b[2].map((k) => [k, k] as const), bk[b[0]] ?? b[1], (v) => { setBk((o) => ({ ...o, [b[0]]: v })); setSheet(null); say(`Backup ${b[0]} → ${v}`); }) };
    }
    if (kind === 'del') return { title: 'Uỷ quyền đang hiệu lực', sub: 'Tra cứu có hạn · hết hạn việc tự về người cũ', node: (
      <>
        <div className={s.shNote}>Phiếu chờ anh duyệt trong kỳ vắng tự chuyển lên cấp trên.</div>
        <div className={s.shRows}>
          {dl.length ? dl.map(([k, sub], i) => <SRow key={k} k={k} sub={sub}><button type="button" className={s.mini} onClick={() => { setDl(dl.filter((_, j) => j !== i)); say('Đã kết thúc uỷ quyền'); }}>Kết thúc</button></SRow>) : <SRow k="Chưa có uỷ quyền" sub="Khi nghỉ phép, việc tự chuyển theo backup" />}
        </div>
        <button type="button" className={s.main} onClick={() => { setDl([['Tất cả ghế → backup', 'Hôm nay 23/Oct/2026 · bổ sung đơn nghỉ trong 2 ngày làm việc'], ...dl]); setSheet(null); say('Đã báo vắng · việc chuyển cho backup'); }}>Vắng hôm nay</button>
      </>) };
    if (kind === 'sess') return { title: 'Phiên & thiết bị', sub: 'Đăng xuất máy bị mất hoặc lạ', node: (
      <>
        <div className={`${s.shNote} ${s.shNoteW}`}>Đăng xuất thiết bị chấm công thì đăng ký lại cần HR duyệt.</div>
        <div className={s.shRows}>
          {sessions.map(([k, sub, me]) => <SRow key={k} k={k} sub={sub}>{!me && <button type="button" className={`${s.mini} ${s.miniErr}`} onClick={() => { setGone((g) => [...g, k]); say(`Đã đăng xuất ${k}`); }}>Đăng xuất</button>}</SRow>)}
        </div>
        {sessions.length > 1 && <button type="button" className={s.main} onClick={() => { setGone(PH_SESSIONS.filter((x) => !x[2]).map((x) => x[0])); setSheet(null); say('Đã đăng xuất mọi máy khác'); }}>Đăng xuất mọi máy khác</button>}
      </>) };
    return { title: 'Ghế đang giữ', sub: 'HR cập nhật theo quyết định nhân sự', node: (
      <>
        <div className={s.shRows}>{PH_SEAT_ROWS.map(([k, sub]) => <SRow key={k} k={k} sub={sub} />)}</div>
        <div className={s.shNote} style={{ marginTop: 10 }}>Việc anh vừa đề nghị vừa duyệt ở cấp BOD ghi cờ "Tự duyệt".</div>
      </>) };
  })();

  const decide = (x: PhReq, st: PhReq['st'], msg: string) => { setQ((o) => o.map((y) => (y.id === x.id ? { ...y, st, selfDone: !!x.self && st === 'done' } : y))); say(msg); };
  const submitRej = () => {
    if (why.trim().length < 10) { setWhyTried(true); return; }
    const x = q.find((y) => y.id === rej);
    if (x) decide(x, 'rej', `Đã từ chối ${x.id} · báo người tạo`);
    setRej(null); setWhy(''); setWhyTried(false);
  };

  const list = (
    <div className={s.page}>
      <StateView state={state === 'empty' || state === 'noperm' ? 'ready' : state} title={state === 'error' ? t('setph.errT') : undefined} action={state === 'error' ? <button type="button" className={s.btn} onClick={() => location.reload()}><IconPlayerPlay size={16} aria-hidden="true" />{t('setph.retry')}</button> : undefined}
        skeleton={<div aria-busy="true" className={s.page}>{[1, 2, 3, 4].map((i) => <div key={i} className={s.sk}><i /><i /><i /></div>)}</div>}>
        <div className={s.prof}>
          <div className={s.profTop}>
            <span className={s.av} aria-hidden="true">DV</span>
            <div className={s.who}><b>DAVID</b><span>gi•••@hanalogisticsvn.com</span></div>
            <button type="button" className={s.btn} onClick={() => say('Ảnh mới đồng bộ lên Outlook / Teams')}><IconArrowsExchange size={16} aria-hidden="true" />{t('setph.photo')}</button>
          </div>
          <div className={s.chips}>{PH_SEATS.map(([k, tn]) => <span key={k} className={`${s.chip} ${TONE[tn] ?? ''}`}>{k}</span>)}</div>
        </div>
        <Group h="Hồ sơ">
          <Row k="Ghế đang giữ" sub="SALE-MGR · BOD-DDIR (quyền) · ADMIN" r="3" onTap={() => setSheet('seats')} />
          <a className={`${s.row} ${s.link}`} href="/mee"><span className={s.rl}>{t('setph.mee')}</span><span className={s.rr}>{t('setph.meeSub')}</span></a>
        </Group>
        <Group h="Giao diện">
          <Row k="Chế độ màu" r={dark ? 'Tối' : 'Sáng'} onTap={() => setSheet('theme')} />
          <Row k="Ngôn ngữ" r={lang === 'vi' ? 'Tiếng Việt' : 'English'} onTap={() => setSheet('lang')} />
        </Group>
        <Group h="Bong bóng Hei"><Row k="Bong bóng Hei" r={bub ? (dnd ? 'Không làm phiền' : 'Bật') : 'Tắt'} onTap={() => setSheet('hei')} /></Group>
        <Group h="Thông báo">{PH_NOTI.map((n) => <Row key={n.id} k={n.k} sub={n.s} subWarn={n.locked} r={phNotiSum(n, nOv)} onTap={() => setSheet('noti:' + n.id)} />)}</Group>
        <Group h="Backup & uỷ quyền">
          {PH_BACKUP.map(([seat, def]) => <Row key={seat} k={`Backup ${seat}`} r={bk[seat] ?? def} onTap={() => setSheet('bk:' + seat)} />)}
          <Row k="Uỷ quyền đang hiệu lực" sub={dl[0]?.[0] ?? 'Chưa có'} r={String(dl.length)} onTap={() => setSheet('del')} />
        </Group>
        <Group h="Bảo mật"><Row k="Phiên & thiết bị" sub="Đăng xuất từ xa" r={`${sessions.length} máy`} onTap={() => setSheet('sess')} /></Group>
        <Group h="Quản trị"><Row k="Duyệt thay đổi quản trị" sub={noperm ? 'Không có quyền' : 'Cũ → mới · Đồng ý / Từ chối'} badge={pend.length > 0 && !noperm ? String(pend.length) : undefined} onTap={() => setView('appr')} /></Group>
      </StateView>
    </div>
  );

  const shown = q.filter((x) => (qTab === 'pend' ? x.st === 'pend' && x.mine : x.st !== 'pend'));
  const appr = (
    <div className={s.page}>
      <div className={s.head}>
        <button type="button" className={s.back} aria-label={t('setph.back')} onClick={() => setView('list')}><IconChevronLeft size={18} stroke={2.2} aria-hidden="true" /></button>
        <div><h1>{t('setph.apprT')}</h1><div>Quản trị · {pend.length} chờ anh</div></div>
      </div>
      <div role="tablist" aria-label={t('setph.filter')} className={s.tabs}>
        <button type="button" role="tab" className={s.tab} aria-selected={qTab === 'pend'} onClick={() => setQTab('pend')}>{t('setph.tabPend', { n: pend.length })}</button>
        <button type="button" role="tab" className={s.tab} aria-selected={qTab === 'done'} onClick={() => setQTab('done')}>{t('setph.tabDone')}</button>
      </div>
      {state === 'loading' && [1, 2, 3, 4].map((i) => <div key={i} aria-busy="true" className={s.sk}><i /><i /><i /></div>)}
      {state === 'error' && <div role="alert" className={s.msg}><b>{t('setph.apprErr')}</b><span>{t('setph.apprErrS')}</span><button type="button" className={s.btn} onClick={() => location.reload()}><IconPlayerPlay size={16} aria-hidden="true" />{t('setph.retry')}</button></div>}
      {noperm && <div className={s.msg}><b>{t('setph.noPermT')}</b></div>}
      {state !== 'loading' && state !== 'error' && !noperm && (shown.length === 0
        ? <div className={s.msg}><b>{qTab === 'pend' ? 'Không còn thay đổi chờ anh duyệt' : 'Chưa có phiếu đã xử lý'}</b></div>
        : shown.map((x) => (
          <article key={x.id} className={s.card}>
            <div className={s.cardTop}><span>{x.id}</span><span>{x.when}</span></div>
            <b>{x.k}</b>
            <div className={s.ab}><span className={s.old}>{x.a}</span><span aria-hidden="true">→</span><span className={s.new}>{x.b}</span></div>
            <div className={s.by}>{x.by} · "{x.why}"</div>
            {x.flag && x.st === 'pend' && <div className={s.flag}>{x.flag}</div>}
            {x.st === 'pend' ? (
              <div className={s.acts}>
                <button type="button" className={s.act} onClick={() => setRej(x.id)}><IconX size={16} aria-hidden="true" />{t('setph.reject')}</button>
                <button type="button" className={`${s.act} ${s.actMain}`} onClick={() => decide(x, 'done', `${x.self ? 'Đã tự duyệt · ghi cờ · ' : 'Đã duyệt · áp ngay · '}${x.id}`)}><IconCheck size={16} aria-hidden="true" />{x.self ? t('setph.selfAgree') : t('setph.agree')}</button>
              </div>
            ) : (
              <div className={s.stRow}>
                <span className={`${s.st} ${x.st === 'done' ? s.stOk : x.st === 'undone' ? s.stMute : s.stErr}`}>{x.st === 'done' ? (x.selfDone ? 'Đã áp · cờ Tự duyệt' : 'Đã áp') : x.st === 'undone' ? 'Đã hoàn tác' : 'Từ chối'}</span>
                {x.st === 'done' && <button type="button" className={s.btn} onClick={() => decide(x, 'undone', `Đã hoàn tác ${x.id} · giá trị cũ khôi phục`)}><IconArrowBackUp size={16} aria-hidden="true" />{t('setph.undo')}</button>}
              </div>
            )}
          </article>
        )))}
    </div>
  );

  return (
    <Page>
      {view === 'list' && <PageHeader title={t('setph.title')} actions={<HeaderTool label={t('setph.theme')} icon={dark ? IconSun : IconMoon} onClick={() => setDark(!dark)} />} />}
      {view === 'list' ? list : appr}
      <Drawer open={!!body} onOpenChange={(o) => { if (!o) setSheet(null); }} title={body?.title ?? ''} description={body?.sub || undefined}>{body?.node}</Drawer>
      <Drawer open={!!rej} onOpenChange={(o) => { if (!o) { setRej(null); setWhy(''); setWhyTried(false); } }} title={t('setph.rejectT', { id: rej ?? '' })}
        footer={<button type="button" className={`${s.act} ${s.actMain}`} onClick={submitRej}><IconX size={16} aria-hidden="true" />{t('setph.reject')}</button>}>
        <div className={s.quick}>{PH_QUICK.map((k) => <button key={k} type="button" className={s.q} onClick={() => setWhy(k)}>{k}</button>)}</div>
        <textarea className={`${s.ta} ${whyTried && why.trim().length < 10 ? s.taErr : ''}`} aria-label="Lý do" placeholder={t('setph.reason')} value={why} onChange={(e) => setWhy(e.target.value)} />
      </Drawer>
    </Page>
  );
}
